/**
 *  masthead.ts
 *
 *  Copyright (c) 2017 by General Electric Company. All rights reserved.
 *
 *  The copyright to the computer software herein is the property of
 *  General Electric Company. The software may be used and/or copied only
 *  with the written permission of General Electric Company or in accordance
 *  with the terms and conditions stipulated in the agreement/contract
 *  under which the software has been supplied.
 *
 */
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild
} from '@angular/core';

import { WFMComponent } from '../wfm.component';

const breakpoints = {
  edsBreakpointBase: '0',
  edsBreakpointLandscapeLarge: '400px',
  edsBreakpointLandscapeMedium: '360px',
  edsBreakpointLandscapeXlarge: '1024px',
  edsBreakpointLarge: '1280px',
  edsBreakpointMedium: '960px',
  edsBreakpointPortraitLarge: '720px',
  edsBreakpointPortraitMedium: '480px',
  edsBreakpointPortraitXlarge: '720px',
  edsBreakpointSmall: '600px',
  edsBreakpointSmallest: '320px',
  edsBreakpointXlarge: '1440px',
  edsBreakpointXxlarge: '1920px'
};

/**
 * @description: The **masthead** is the top-most element of the application interface and
 * is persistently displayed across all screens of the application user experience.
 * It is where references to the name of the application and brand are most commonly represented.
 * The masthead may also serve as the primary container for key application-wide tools such as navigation,
 * search, notifications, and access to user settings.
 *
 * @browsers chrome, safari, firefox, ie11, edge
 * @usage
 *
 * The masthead component allows you to specify the header with customer labeling,
 * logo image and any inner content.
 *
 *```html
 * <eds-masthead
 *    name="Your Title"
 *    type="default"
 *    [forceShowHamburger]="true"
 *    [isBrandLink]="true"
 *    [icon]="<icon-name>"
 *    [status]="[
 *     { name: 'Status', content: 'Ready' },
 *     { name: 'Mode', content: 'Research' },
 *     { name: 'Version', content: '5.4' }
 *    ]"
 *    (isToggleIconHamburgerChange)="toggleIconHamburgerChange(event)"
 *    (brandLinkClick)="brandLinkClick()"
 *    >
 *    <span>Any content to render as innerHTML</span>
 * </eds-masthead>
 *```
 *
 * Masthead component has 3 named slots that you can use: navigation, left-content, and utilities.
 * `navigation` slot can be used to integrate the navigation into the masthead
 * `left-content` slot can be used to align content left right after branding area (logo and the title)
 * `utilities` slot can be used to right align content in the masthead, where you might use icons or other components
 *
 *  Here is an example of all 3 slots being used. You can mix and match slots by your design needs:
 *
 * ```html
 * <eds-masthead>
 *      <div slot="left-content">
 *        <span>Left Content Slot</span>
 *      </div>
 *      <div slot="navigation">
 *        <span>Navigation Slot</span>
 *       </div>
 *      <div slot="utilities">
 *        <span>Utilities Slot</span>
 *       </div>
 * </eds-masthead>
 *
 *```
 * In the demo about all 3 slots are shown regardless of the screen size, but based on the use cases, slot content will be
 * hidden or moved to different location based on screen size. For example, in tablet and mobile screen sizes, navigation slot
 * content will be hidden in the masthead and moves into navigaton component. Moving of the contents in the slots will be done
 * by the application that will be integrating masthead with navigation component. This will shown in detail in the examples section.
 *
 */
@Component({
  selector: 'wfm-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})

/**
 * @class: EDSMasthead
 * @description: Angular element for eds-masthead
 **/
export class HeaderComponent extends WFMComponent implements OnChanges, AfterViewInit {
  /**
   * @description to set based on if that left-content slot is being used and has content
   */
  get leftContentPresent(): boolean {
    return this.leftContent && this.leftContent.nativeElement.children.length > 0;
  }

  /**
   * @description to set based on if that navigation slot is being used and has content
   */
  get navPresent(): boolean {
    return this.navigation && this.navigation.nativeElement.children.length > 0;
  }

  /**
   * @description to set based on if that utilities slot is being used and has content
   */
  get utilitiesPresent(): boolean {
    return this.utilities && this.utilities.nativeElement.children.length > 0;
  }

  constructor() {
    super();
  }

  /**
   * `default` | `skinny`
   */
  @Input() type = 'default';

  @Input() name: string;

  /**
   * sidebar | default
   */
  @Input() location: string;

  /**
   *  ```
   * 'icon': {
   *  'filename': ,
   *  'svgString': ,
   *  'svgStyle': ,
   *  'baseSvgPath':
   * }
   * ```
   */
  @Input() icon = 'ico-ge-healthcare-standard-logo';

  /**
   * @description Array of status items to display in the footer
   * @type {object}
   */
  @Input() status: any[];

  /**
   * @description boolean to force hamburger on/off in the event `slot=navigation` is not used
   */
  @Input() forceShowHamburger = false;

  @Input() isEnablePerformanceHooks = false;

  @ViewChild('slotLeftContent') leftContent: ElementRef;

  @ViewChild('slotNavigation') navigation: ElementRef;

  @ViewChild('slotUtilities') utilities: ElementRef;

  @ViewChild('performanceHooks') performanceHooks: ElementRef;

  /**
   * Option to force the navigation trigger icon to be either hamburger icon (false) or close icon (true)
   * @description
   * @type {string}
   */
  @Input() isToggleIconHamburger = false;
  @Output() isToggleIconHamburgerChange = new EventEmitter<boolean>();

  /**
   * @description Option to change pointer upon hovering brand name or icon
   */
  @Input() isBrandLink = false;

  @Output() brandLinkClick = new EventEmitter();

  /**
   * @description to set the masthead css class based on type
   */
  mastheadCssClass: string;

  /**
   * @description width of the window at any given time
   */
  winWidth;

  /**
   * @description to specify what type of screen type based on breakpoints: desktop, tablet, or mobile
   */
  screen = '';

  /**
   * @description to set the branding classes based on navigation flag
   */
  mastheadBrandingClass = 'masthead__branding';

  /**
   * @description set max width if slots present
   */
  mastheadBrandingClassMaxWidth: string;

  /**
   * @description sets class if navigation slotchange event is fired and slot is used
   */
  mastheadNavigationClass = 'masthead__content--navigation';

  /**
   * @description sets class if utilities slotchange event is fired and slot is used
   */
  mastheadUtitilesClass = 'masthead__content--utilities';

  iconSvgStyle = { fill: 'var(--eds-masthead-text)', 'width.px': '24', 'height.px': '24' };

  public responsiveBehaviorsDebounce = WFMComponent.debounce(() => {
    this.responsiveBehaviors();
  }, 500);

  /**
   * ngOnChanges
   */
  ngOnChanges() {
    this.updateStyle();
  }

  /**
   * ngAfterViewInit
   */
  ngAfterViewInit() {
    this.winWidth = window.innerWidth;

    this.responsiveBehaviors();
  }

  onHamburgerClickHandler() {
    this.isToggleIconHamburger = !this.isToggleIconHamburger;
    this.isToggleIconHamburgerChange.emit(this.isToggleIconHamburger);
  }

  /**
   * @description fires eds-masthead-brand-clicked (icon or name)
   */
  onBrandClickHandler() {
    this.isBrandLink && this.brandLinkClick.emit();
  }

  /**
   * @function: responsiveBehaviors
   * @description: compares window width with breakpoints and updates screen type and fires eds-masthead-responsive-screen-change event
   **/
  responsiveBehaviors() {
    if (window.innerWidth < parseInt(breakpoints.edsBreakpointSmall, 10)) {
      if (this.screen !== 'mobile') {
        this.screen = 'mobile';
      }
    } else if (window.innerWidth < parseInt(breakpoints.edsBreakpointMedium, 10)) {
      if (this.screen !== 'tablet') {
        this.screen = 'tablet';
      }
    } else if (window.innerWidth >= parseInt(breakpoints.edsBreakpointMedium, 10)) {
      if (this.screen !== 'desktop') {
        this.screen = 'desktop';
      }
    }
  }

  /**
   * @function: toggleHamburger
   * @description: toggles showing (tablet or mobile mode when nav is present) and hiding (desktop mode or nav is absent) hamburger icon
   **/
  toggleHamburger(screen: string, navPresent: any) {
    return navPresent && (screen === 'tablet' || screen === 'mobile');
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // Run our responsive behaviors on window resize
    window.requestAnimationFrame(() => {
      if (this.winWidth !== event.target.innerWidth) {
        this.winWidth = event.target.innerWidth;
        this.responsiveBehaviorsDebounce();
      }
    });
  }

  /**
   * @function updateStyle
   * @description: This function updates the styles based on input changes to the masthead's type attribute
   * @return {string} css class based on masthead type
   */
  updateStyle() {
    switch (this.type) {
      case 'skinny':
        this.mastheadCssClass = 'masthead masthead--skinny';
        break;
      case 'default':
        this.mastheadCssClass = 'masthead';
        break;
      default:
        return;
    }

    this.mastheadBrandingClass = 'masthead__branding';

    if (this.navPresent) {
      this.mastheadBrandingClassMaxWidth = '';
    } else {
      this.mastheadBrandingClassMaxWidth = 'inherit';
    }

    if (this.location === 'sidebar') {
      this.mastheadCssClass = this.mastheadCssClass + ' masthead--no-shadow';
    } else {
      this.mastheadCssClass = this.mastheadCssClass.replace('masthead--no-shadow', '');
    }
  }
}
